import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import FilterBar, { type FilterState } from "@/components/feature/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import { videos } from "@/mocks/videos";
import { filterVideos, sortVideos } from "@/utils/videoFilter";

function readFilters(params: URLSearchParams): Omit<FilterState, "genre" | "sort"> {
  return {
    type: params.get("type") || "全部",
    region: params.get("region") || "全部",
    year: params.get("year") || "全部",
  };
}

const hotVideos = [...videos].sort((a, b) => b.rating - a.rating).slice(0, 5);

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const partial = readFilters(searchParams);
  const [input, setInput] = useState(keyword);

  const filters: FilterState = { ...partial, genre: "全部", sort: "热度" };
  const hasCondition = Boolean(keyword.trim()) || filters.type !== "全部" || filters.region !== "全部" || filters.year !== "全部";

  usePageMeta(
    keyword ? `搜索：${keyword} - 芒果视频` : "全站搜索 - 芒果视频",
    `在芒果视频搜索「${keyword || "影视内容"}」，快速找到相关影视剧集、电影、综艺与短视频，支持按类别、地区、年份精准筛选。`,
    `芒果视频,搜索${keyword ? "," + keyword : ""},影视搜索,在线观看,视频推荐`
  );

  const result = useMemo(() => {
    const filtered = filterVideos(videos, { keyword, ...partial });
    return sortVideos(filtered, "热度");
  }, [keyword, partial]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const value = input.trim();
    if (value) {
      next.set("q", value);
    } else {
      next.delete("q");
    }
    next.delete("page");
    setSearchParams(next);
  };

  const updateFilter = (key: keyof FilterState, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "全部") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6 md:py-8 lg:px-10">
      <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">全站搜索</h1>
      <p className="mt-2 text-sm text-foreground-600">输入影视名称、主演或类型关键词，快速定位想看的内容。</p>

      <form
        onSubmit={submitSearch}
        className="mt-5 flex w-full items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2 focus-within:border-primary-500"
      >
        <i className="ri-search-line text-lg text-foreground-500"></i>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          type="text"
          name="q"
          placeholder="搜索影视剧、电影、综艺…"
          className="w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
        />
        <button
          type="submit"
          className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      {keyword.trim() && (
        <p className="mt-4 text-sm text-foreground-600">
          关键词「<span className="text-primary-400">{keyword}</span>」共找到{" "}
          <span className="font-semibold text-foreground-900">{result.length}</span> 条结果
        </p>
      )}

      <div className="mt-5">
        <FilterBar filters={filters} onChange={updateFilter} showGenre={false} showSort={false} />
      </div>

      {result.length > 0 ? (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
          {result.map((item) => (
            <VideoCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="mt-6">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
              {hasCondition ? "没有找到相关的影视内容" : "还没有开始搜索"}
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              {hasCondition
                ? "换个关键词或调整筛选条件再试试吧。"
                : "输入关键词，或直接看看下面这些热门推荐。"}
            </p>
          </div>

          <div className="mt-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">热门影视推荐</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {hotVideos.map((item) => (
                <VideoCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-400"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    </div>
  );
}